export interface Account {
  id: string;
  email: string;
  displayName: string;
  createdAt?: string;
}

export interface AccountSession {
  id: string;
  created_at: string;
  last_used_at: string;
  expires_at: string;
  ip_address: string | null;
  user_agent: string | null;
  current: boolean;
}

export interface Device {
  id: string;
  public_id?: string;
  publicId?: string;
  name: string;
  platform?: string;
  sync_enabled?: boolean;
  syncEnabled?: boolean;
  push_registered?: boolean;
  last_seen_at?: string | null;
  created_at?: string;
  revoked_at?: string | null;
  subscriptions?: DeviceSubscription[];
  default_sms_subscription_id?: number | null;
  relayEnabled?: boolean;
  relayPriority?: number;
  relaySubscriptionFingerprint?: string | null;
  relayCapable?: boolean;
  relayIssue?: string | null;
  simChangePending?: boolean;
  simChangedAt?: string | null;
  batteryLevel?: number | null;
  batteryCharging?: boolean | null;
  batteryUpdatedAt?: string | null;
}

export interface DeviceSubscription {
  id: number;
  slotIndex: number;
  displayName: string;
  carrierName: string;
  identityFingerprint?: string;
}

export interface ApkRelease {
  version: string;
  buildNumber: number;
  sha256: string;
  bytes: number;
  downloadUrl: string;
  publishedAt: string;
}

export type PluginRelease = Omit<ApkRelease, 'buildNumber'>;

export interface Conversation {
  id: string;
  normalized_address: string;
  display_name: string | null;
  archived: boolean;
  trashed_at: string | null;
  pinned: boolean;
  blocked: boolean;
  unread_count: number;
  last_message_at: string;
  last_message_body: string | null;
  last_message_direction: 'incoming' | 'outgoing' | null;
  last_message_status: string | null;
}

export interface Message {
  id: string;
  direction: 'incoming' | 'outgoing';
  body: string;
  occurred_at: string;
  status: string;
  origin: string;
  subscription_id: number | null;
  error_code: string | null;
}

export interface Contact {
  id: string;
  phone: string;
  display_name: string;
  consent_status: 'unknown' | 'opted_in' | 'opted_out';
  consent_source: string | null;
  consent_recorded_at: string | null;
  opted_out_at: string | null;
}

export interface Campaign {
  id: string;
  device_id: string;
  name: string;
  body_template: string;
  state: string;
  recipient_count: number;
  scheduled_at: string | null;
  confirmed_at: string | null;
  created_at: string;
}

export function activeDeviceId(devices: Device[]): string | null {
  return devices.find((device) => (device.sync_enabled ?? device.syncEnabled) && !device.revoked_at)?.id ?? null;
}

export function activeDevices(devices: Device[]): Device[] {
  return devices.filter((device) => (device.sync_enabled ?? device.syncEnabled) && !device.revoked_at);
}

export function shouldSubmitComposer(key: string, shiftKey: boolean, isComposing: boolean): boolean {
  return key === 'Enter' && !shiftKey && !isComposing;
}

export function displayAddress(conversation: Conversation): string {
  return conversation.display_name?.trim() || conversation.normalized_address;
}

export function formatRelative(value: string | null | undefined): string {
  if (!value) return 'Never';
  const timestamp = new Date(value).getTime();
  const difference = timestamp - Date.now();
  const absolute = Math.abs(difference);
  const formatter = new Intl.RelativeTimeFormat(undefined, { numeric: 'auto' });
  if (absolute < 60_000) return formatter.format(Math.round(difference / 1000), 'second');
  if (absolute < 3_600_000) return formatter.format(Math.round(difference / 60_000), 'minute');
  if (absolute < 86_400_000) return formatter.format(Math.round(difference / 3_600_000), 'hour');
  return formatter.format(Math.round(difference / 86_400_000), 'day');
}

export function formatLocalDateTimeInput(value: Date): string {
  const pad = (part: number) => part.toString().padStart(2, '0');
  return `${value.getFullYear()}-${pad(value.getMonth() + 1)}-${pad(value.getDate())}T${pad(value.getHours())}:${pad(value.getMinutes())}`;
}

export function isApkRelease(value: unknown): value is ApkRelease {
  if (!value || typeof value !== 'object') return false;
  const release = value as Partial<ApkRelease>;
  return typeof release.version === 'string' && release.version.length > 0
    && Number.isInteger(release.buildNumber) && Number(release.buildNumber) > 0
    && typeof release.sha256 === 'string' && /^[a-f\d]{64}$/i.test(release.sha256)
    && typeof release.bytes === 'number' && release.bytes > 0
    && release.downloadUrl === '/download/messavo.apk'
    && typeof release.publishedAt === 'string' && !Number.isNaN(Date.parse(release.publishedAt));
}

export function isPluginRelease(value: unknown): value is PluginRelease {
  if (!value || typeof value !== 'object') return false;
  const release = value as Partial<PluginRelease>;
  return typeof release.version === 'string' && /^\d+\.\d+\.\d+$/.test(release.version)
    && typeof release.sha256 === 'string' && /^[a-f\d]{64}$/i.test(release.sha256)
    && typeof release.bytes === 'number' && release.bytes > 0
    && release.downloadUrl === '/download/messavo-woocommerce.zip'
    && typeof release.publishedAt === 'string' && !Number.isNaN(Date.parse(release.publishedAt));
}

export function formatFileSize(bytes: number): string {
  if (bytes < 1_048_576) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
  return `${(bytes / 1_048_576).toFixed(1)} MB`;
}

export function formatBattery(level: number | null | undefined, charging: boolean | null | undefined): string {
  if (level == null || !Number.isFinite(level)) return 'Battery unavailable';
  const percent = Math.max(0, Math.min(100, Math.round(level)));
  return `${percent}%${charging ? ' · Charging' : ''}`;
}

export function isRelayReady(device: Device, now = Date.now()): boolean {
  if (!device.relayCapable || device.simChangePending || !device.last_seen_at) return false;
  const lastSeen = new Date(device.last_seen_at).getTime();
  return Number.isFinite(lastSeen) && lastSeen >= now - 60_000;
}
